function asDate(value) {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value.toDate === "function") return value.toDate()
  if (typeof value === "number" || typeof value === "string") {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
  }
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000)
  return null
}

function recordDate(record, fields) {
  for (const field of fields) {
    const date = asDate(record && record[field])
    if (date) return date
  }
  return null
}

function newestFirst(records, fields) {
  return [...records].sort((a, b) => {
    const aDate = recordDate(a, fields)
    const bDate = recordDate(b, fields)
    return (bDate ? bDate.getTime() : 0) - (aDate ? aDate.getTime() : 0)
  })
}

const LINE_SOURCES = [
  { key: "manual", label: "Manual" },
  { key: "gopro", label: "GoPro" },
  { key: "iracing", label: "iRacing" },
  { key: "alfano", label: "Alfano" },
  { key: "other", label: "Other import" },
]

function lineSource(line = {}) {
  const explicit = String(line.creationSource || "").trim().toLowerCase()
  const known = LINE_SOURCES.find(source => source.key === explicit)
  if (known) return { ...known }
  if (explicit) return { ...LINE_SOURCES[LINE_SOURCES.length - 1] }

  const telemetryId = String(line.telemetryId || "").trim().toLowerCase()
  if (telemetryId.startsWith("gopro-")) return { ...LINE_SOURCES[1] }
  if (telemetryId.startsWith("alfano-")) return { ...LINE_SOURCES[3] }
  if (telemetryId) return { ...LINE_SOURCES[2] }
  return { ...LINE_SOURCES[0] }
}

function isAdminLineVisible(line) {
  if (!line) return false
  return lineSource(line).key !== "manual" || line.isCloseDetected === true
}

function buildLineSources(lines) {
  const counts = new Map(LINE_SOURCES.map(source => [source.key, 0]))
  lines.forEach(line => {
    const source = lineSource(line)
    counts.set(source.key, counts.get(source.key) + 1)
  })
  const total = lines.length
  return LINE_SOURCES.map(source => ({
    ...source,
    count: counts.get(source.key),
    percent: total ? Math.round((counts.get(source.key) / total) * 100) : 0,
  }))
}

function isPublicProfile(user) {
  return Boolean(user && user.uid && (user.displayName || user.photoURL))
}

function buildAccountCreationByMonth(users, now) {
  const end = new Date(now)
  end.setUTCDate(1)
  end.setUTCHours(0, 0, 0, 0)
  const months = []

  for (let offset = 11; offset >= 0; offset -= 1) {
    const date = new Date(end)
    date.setUTCMonth(end.getUTCMonth() - offset)
    months.push({
      key: date.toISOString().slice(0, 7),
      label: date.toLocaleDateString("en", { month: "short", timeZone: "UTC" }),
      count: 0,
    })
  }

  const byKey = new Map(months.map(month => [month.key, month]))
  users.forEach(user => {
    const date = recordDate(user, ["createdAt", "created"])
    const month = date && byKey.get(date.toISOString().slice(0, 7))
    if (month) month.count += 1
  })
  return months
}

function decodeFirestoreValue(value = {}) {
  if (Object.prototype.hasOwnProperty.call(value, "nullValue")) return null
  if (Object.prototype.hasOwnProperty.call(value, "stringValue")) return value.stringValue
  if (Object.prototype.hasOwnProperty.call(value, "booleanValue")) return value.booleanValue
  if (Object.prototype.hasOwnProperty.call(value, "integerValue")) return Number(value.integerValue)
  if (Object.prototype.hasOwnProperty.call(value, "doubleValue")) return Number(value.doubleValue)
  if (Object.prototype.hasOwnProperty.call(value, "timestampValue")) return value.timestampValue
  if (Object.prototype.hasOwnProperty.call(value, "geoPointValue")) return value.geoPointValue
  if (value.arrayValue) return (value.arrayValue.values || []).map(decodeFirestoreValue)
  if (value.mapValue) {
    return Object.fromEntries(
      Object.entries(value.mapValue.fields || {}).map(([key, item]) => [key, decodeFirestoreValue(item)])
    )
  }
  return undefined
}

function decodeFirestoreDocument(document = {}) {
  const encodedId = String(document.name || "").split("/").pop()
  let id = encodedId
  try { id = decodeURIComponent(encodedId) } catch (_) {}
  const data = Object.fromEntries(
    Object.entries(document.fields || {}).map(([key, value]) => [key, decodeFirestoreValue(value)])
  )
  return {
    id,
    username: id,
    ...data,
    createdAt: document.createTime || data.createdAt || data.created,
  }
}

function approveTrackUpdate(serverTimestamp) {
  return {
    status: "published",
    publishedAt: serverTimestamp,
    updatedAt: serverTimestamp,
  }
}

function lineUrl(line) {
  if (!line) return "/r"

  const id = encodeURIComponent(String(line.id || ""))
  const storedUrl = typeof line.url === "string" ? line.url.replace(/\/$/, "") : ""
  const trackPath = storedUrl.match(/^(\/go-karting\/[^/?#]+)(?:\/r\/[^/?#]+)?$/)

  if (!trackPath) return `/r/${id}`
  return `${trackPath[1]}/r/${id}`
}

function activityEntry(kind, record, date, title, detail, href) {
  return {
    id: `${kind}-${record.id}`,
    kind,
    title,
    detail,
    href,
    date,
    record,
  }
}

function buildActivity({ tracks, lines, users }) {
  const entries = []

  tracks.forEach(track => {
    if (track.status !== "published" && track.status !== "pending") return
    const date = recordDate(track, ["createdAt", "updatedAt"])
    if (!date) return
    const pending = track.status === "pending"
    entries.push(activityEntry(
      pending ? "suggestion" : "track",
      track,
      date,
      pending ? "New track suggestion" : "Track added",
      track.name || track.id,
      pending ? null : track.slug ? `/go-karting/${encodeURIComponent(track.slug)}` : null,
    ))
  })

  lines.forEach(line => {
    const date = recordDate(line, ["created", "createdAt"])
    if (!date) return
    entries.push(activityEntry(
      "line",
      line,
      date,
      "Racing line uploaded",
      line.name || line.id,
      lineUrl(line),
    ))
  })

  users.forEach(user => {
    const date = recordDate(user, ["createdAt", "created"])
    if (!date) return
    entries.push(activityEntry(
      "member",
      user,
      date,
      "Member joined",
      user.displayName || user.username || user.id,
      user.username || user.id ? `/p/${encodeURIComponent(user.username || user.id)}` : null,
    ))
  })

  return entries.sort((a, b) => b.date.getTime() - a.date.getTime())
}

function buildActivityByDay(activity, now) {
  const days = []
  const end = new Date(now)
  end.setUTCHours(0, 0, 0, 0)

  for (let offset = 6; offset >= 0; offset -= 1) {
    const date = new Date(end)
    date.setUTCDate(end.getUTCDate() - offset)
    const key = date.toISOString().slice(0, 10)
    days.push({
      key,
      label: date.toLocaleDateString("en", { weekday: "short", timeZone: "UTC" }),
      count: 0,
    })
  }

  const byKey = new Map(days.map(day => [day.key, day]))
  activity.forEach(item => {
    const day = byKey.get(item.date.toISOString().slice(0, 10))
    if (day) day.count += 1
  })

  return days
}

function buildAdminDashboard({ tracks = [], lines = [], users = [], claims = [], sponsorships = [], stats = {}, now = new Date() }) {
  const visibleLines = lines.filter(isAdminLineVisible)
  const pendingTracks = newestFirst(
    tracks.filter(track => track.status === "pending"),
    ["createdAt", "updatedAt"],
  )
  const pendingClaims = newestFirst(
    claims.filter(claim => claim.status === "pending"),
    ["createdAt", "updatedAt"],
  )
  const pendingSponsorships = newestFirst(
    sponsorships.filter(sponsorship => sponsorship.status === "pending"),
    ["createdAt", "updatedAt"],
  )
  const publishedTracks = tracks.filter(track => track.status === "published")
  const healthTracks = [...publishedTracks, ...pendingTracks]
  const recentTracks = newestFirst(publishedTracks, ["createdAt", "updatedAt"]).slice(0, 8)
  const recentLines = newestFirst(
    visibleLines,
    ["created", "createdAt"],
  ).slice(0, 8)
  const profiles = users.filter(isPublicProfile)
  const recentUsers = newestFirst(profiles, ["createdAt", "created"]).slice(0, 8)
  const activity = buildActivity({ tracks, lines: visibleLines, users: profiles })
  const lineSources = stats.lineSources || buildLineSources(visibleLines)
  const accountCreationMonths = stats.accountCreationByMonth || buildAccountCreationByMonth(profiles, now)

  return {
    metrics: {
      publishedTracks: publishedTracks.length,
      pendingSuggestions: pendingTracks.length,
      pendingClaims: pendingClaims.length,
      pendingSponsorships: pendingSponsorships.length,
      racingLines: stats.totalLines == null ? visibleLines.length : stats.totalLines,
      racingLinesPeriod: stats.lineMetricLabel || "Total",
      members: stats.totalAccounts == null ? profiles.length : stats.totalAccounts,
    },
    pendingTracks,
    pendingClaims,
    pendingSponsorships,
    recentTracks,
    recentLines,
    recentUsers,
    users: profiles,
    lineSources,
    lineSourcePeriods: stats.lineSourcePeriods || [{
      key: "all",
      label: "Total",
      total: stats.totalLines == null ? visibleLines.length : stats.totalLines,
      sources: lineSources,
    }],
    accountCreationByMonth: accountCreationMonths,
    accountRecency: stats.accountRecency || { last24Hours: 0, last7Days: 0 },
    recentAccounts: stats.recentAccounts || [],
    accountCreationPeriods: stats.accountCreationPeriods || [{
      key: "12m",
      label: "Last 12 months",
      total: accountCreationMonths.reduce((sum, month) => sum + month.count, 0),
      months: accountCreationMonths,
    }],
    activity: activity.slice(0, 20),
    activityByDay: buildActivityByDay(activity, now),
    dataHealth: {
      totalTracks: healthTracks.length,
      missingCountry: healthTracks.filter(track => !track.country).length,
      indoorTracks: healthTracks.filter(track => track.subtype === "indoor").length,
      outdoorTracks: healthTracks.filter(track => track.subtype === "outdoor").length,
    },
  }
}

module.exports = {
  approveTrackUpdate,
  asDate,
  buildAdminDashboard,
  decodeFirestoreDocument,
  isAdminLineVisible,
  lineSource,
  lineUrl,
}
